import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Building2, Loader2, Tag, MapPin, Hash, Image as ImageIcon, Info, Trash2, Landmark, Briefcase, Settings } from 'lucide-react';
import Swal from 'sweetalert2';
import { otherCrmCreateEntity, otherCrmUpdateEntity, otherCrmUploadLogo, otherCrmDeleteLogo, otherCrmDeleteEntity } from '../../../../services/otherCRMService';

import TooltipHelper from '../../../../components/common/TooltipHelper';

/* Segmented / Toggle bonito (igual que AddEntityModal) */
const Segmented = ({ value, onChange, options, ariaLabel }) => (
  <div
    role="tablist"
    aria-label={ariaLabel}
    className="inline-flex flex-wrap gap-1 rounded-2xl border border-gray-200 bg-white p-1"
  >
    {options.map((opt) => {
      const active = value === opt.value;
      return (
        <button
          key={opt.value}
          type="button"
          role="tab"
          aria-selected={active}
          onClick={() => onChange(opt.value)}
          className={[
            "inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition",
            active
              ? "bg-gradient-to-r from-[#0c3b66] to-[#0488AF] text-white shadow"
              : "text-gray-700 hover:bg-gray-50",
          ].join(" ")}
          title={opt.label}
        >
          {opt.icon}
          <span>{opt.label}</span>
        </button>
      );
    })}
  </div>
);

const compact = (obj) =>
  Object.fromEntries(
    Object.entries(obj).filter(([_, v]) => v !== undefined && v !== null && String(v).trim() !== '')
  );

export default function CreateOtherEntityModal({ open = true, onClose, onCreated, onDeleted, entityToEdit = null }) {
  const [busy, setBusy] = useState(false);

  // Campos básicos
  const [tradeName, setTradeName] = useState('');
  const [region, setRegion] = useState('');
  const [district, setDistrict] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);
  const [isPublic, setIsPublic] = useState(true);
  const [entityType, setEntityType] = useState('');
  const [isFavorite, setIsFavorite] = useState(false);
  
  // Tipos de entidad (iniciales + personalizados)
  const DEFAULT_ENTITY_TYPES = [
    { value: 'fundacion', label: 'Fundación' },
    { value: 'corporacion', label: 'Corporación' },
  ];
  const [entityTypes, setEntityTypes] = useState(DEFAULT_ENTITY_TYPES);
  const [newEntityTypeName, setNewEntityTypeName] = useState('');
  const [showNewEntityTypeInput, setShowNewEntityTypeInput] = useState(false);
  const [showEntityTypesModal, setShowEntityTypesModal] = useState(false);

  const handleAddEntityType = () => {
    const trimmed = newEntityTypeName.trim();
    if (!trimmed) {
      Swal.fire('Campo vacío', 'Ingresa un nombre para el tipo de entidad', 'info');
      return;
    }
    const lowerValue = trimmed.toLowerCase().replace(/\s+/g, '_');
    if (entityTypes.some(t => t.value === lowerValue || t.label.toLowerCase() === trimmed.toLowerCase())) {
      Swal.fire('Tipo existente', 'Este tipo de entidad ya existe', 'info');
      return;
    }
    setEntityTypes([...entityTypes, { value: lowerValue, label: trimmed }]);
    setEntityType(lowerValue);
    setNewEntityTypeName('');
    setShowNewEntityTypeInput(false);
  };

  const handleDeleteEntityType = async (typeValue) => {
    // No permitir eliminar tipos por defecto
    if (typeValue === 'fundacion' || typeValue === 'corporacion') {
      Swal.fire('No se puede eliminar', 'Este es un tipo de entidad predeterminado y no se puede eliminar', 'info');
      return;
    }
    
    // Contar tipos personalizados (excluyendo los predeterminados)
    const customTypes = entityTypes.filter(t => t.value !== 'fundacion' && t.value !== 'corporacion');
    
    // Validar que no se intente eliminar el último tipo personalizado
    if (customTypes.length <= 1) {
      Swal.fire({
        icon: 'warning',
        title: 'No se puede eliminar',
        html: 'No se puede eliminar el último tipo de entidad personalizado. Crea una nueva categoría antes de eliminar esta.<br/><br/><strong>Nota:</strong> Las entidades que usan este tipo quedarán sin tipo de entidad asignado.',
        confirmButtonColor: '#0488AF',
      });
      return;
    }
    
    const typeName = entityTypes.find(t => t.value === typeValue)?.label || typeValue;
    
    const result = await Swal.fire({
      title: '¿Eliminar tipo de entidad?',
      html: `¿Estás seguro de eliminar el tipo "${typeName}"?<br/><br/><strong>Nota:</strong> Las entidades que usan este tipo quedarán sin tipo de entidad asignado. Esta acción no se puede deshacer.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });
    
    if (!result.isConfirmed) return;
    
    // Si el tipo a eliminar está seleccionado, limpiar la selección
    if (entityType === typeValue) {
      setEntityType('');
    }
    
    setEntityTypes(entityTypes.filter(t => t.value !== typeValue));
    Swal.fire('Eliminado', `El tipo "${typeName}" ha sido eliminado. Las entidades que lo usaban ahora están sin tipo asignado.`, 'success');
  };

  // Cargar datos si es edición
  useEffect(() => {
    if (entityToEdit) {
      setTradeName(entityToEdit.trade_name || '');
      setRegion(entityToEdit.region || '');
      setDistrict(entityToEdit.district || '');
      const existingLogo = entityToEdit.logo || entityToEdit.logo_url || '';
      setLogoUrl(existingLogo);
      setLogoPreview(existingLogo);
      setLogoFile(null);
      setIsPublic(entityToEdit.is_public ?? true);
      const existingType = entityToEdit.entity_type || '';
      setEntityType(existingType);
      setIsFavorite(entityToEdit.is_favorite ?? false);
      
      // Si el tipo existe pero no está en la lista, agregarlo
      setEntityTypes(prev => {
        if (existingType && !prev.some(t => t.value === existingType)) {
          const label = existingType.charAt(0).toUpperCase() + existingType.slice(1).replace(/_/g, ' ');
          return [...prev, { value: existingType, label }];
        }
        return prev;
      });
    } else {
      // Resetear si no es edición
      setTradeName('');
      setRegion('');
      setDistrict('');
      setLogoUrl('');
      setLogoFile(null);
      setLogoPreview(null);
      setIsPublic(true);
      setEntityType('');
      setEntityTypes(DEFAULT_ENTITY_TYPES);
      setIsFavorite(false);
    }
  }, [entityToEdit, open]);

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!/^image\//.test(file.type)) {
      Swal.fire('Archivo inválido', 'Solo se permiten imágenes (JPG, PNG, etc.)', 'warning');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      Swal.fire('Archivo muy grande', 'El tamaño máximo es 5MB', 'warning');
      return;
    }

    setLogoFile(file);
    const preview = URL.createObjectURL(file);
    setLogoPreview(preview);
  };

  const handleRemoveLogo = async () => {
    if (logoUrl && entityToEdit) {
      const result = await Swal.fire({
        title: '¿Eliminar logo?',
        text: 'Se eliminará el logo de Cloudinary',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
      });

      if (result.isConfirmed) {
        try {
          await otherCrmDeleteLogo(logoUrl);
        } catch (err) {
          console.error('Error al eliminar logo:', err);
        }
      } else {
        return;
      }
    }

    setLogoUrl('');
    setLogoFile(null);
    setLogoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (!entityToEdit?.id) return;
    
    const entityName = entityToEdit.trade_name || entityToEdit.name || 'esta entidad';
    const result = await Swal.fire({
      title: '¿Eliminar entidad?',
      text: `¿Estás seguro de eliminar "${entityName}"? Esta acción eliminará todas las reuniones, minutas y notas asociadas. Esta acción no se puede deshacer.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      try {
        setBusy(true);
        await otherCrmDeleteEntity(entityToEdit.id);
        await Swal.fire('Eliminada', `La entidad "${entityName}" ha sido eliminada`, 'success');
        onDeleted?.(entityToEdit);
        onClose();
      } catch (err) {
        Swal.fire('Error', err?.message || 'No se pudo eliminar la entidad', 'error');
      } finally {
        setBusy(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setBusy(true);
      
      // Subir logo si hay archivo nuevo
      let finalLogoUrl = logoUrl;
      if (logoFile) {
        setUploadingLogo(true);
        try {
          const uploadResult = await otherCrmUploadLogo(logoFile);
          finalLogoUrl = uploadResult?.secureUrl || uploadResult?.secure_url || uploadResult?.url || '';
          if (!finalLogoUrl) {
            throw new Error('No se recibió URL del logo subido');
          }
        } catch (err) {
          Swal.fire('Error', err?.message || 'No se pudo subir el logo', 'error');
          return;
        } finally {
          setUploadingLogo(false);
        }
      }

      const payload = compact({
        trade_name: tradeName?.trim() || undefined,
        region: region?.trim() || undefined,
        district: district?.trim() || undefined,
        ...(finalLogoUrl && finalLogoUrl.trim() ? { logo_url: finalLogoUrl.trim() } : {}),
        is_public: isPublic,
        entity_type: entityType?.trim() || undefined,
        is_favorite: isFavorite,
      });

      let entity;
      if (entityToEdit) {
        const updated = await otherCrmUpdateEntity(entityToEdit.id, payload);
        entity = updated?.data || updated || { ...entityToEdit, ...payload };
        await Swal.fire({
          icon: 'success',
          title: 'Entidad actualizada',
          timer: 1100,
          showConfirmButton: false,
        });
      } else {
      const created = await otherCrmCreateEntity(payload);
        entity = created?.data || created;
      await Swal.fire({
        icon: 'success',
        title: 'Entidad creada',
        timer: 1100,
        showConfirmButton: false,
      });
      }
      onCreated?.(entity);
    } catch (err) {
      Swal.fire('Error', err?.message || `No fue posible ${entityToEdit ? 'actualizar' : 'crear'} la entidad`, 'error');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70]">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute inset-0 flex items-center justify-center p-4 overflow-auto">
        <div className="w-full max-w-4xl rounded-2xl bg-white border shadow-xl overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-white/10 border border-white/20">
                <Building2 className="w-5 h-5" />
              </div>
              <div className="text-lg font-semibold">{entityToEdit ? 'Editar entidad' : 'Agregar nueva entidad'}</div>
            </div>
            <div className="flex items-center gap-2">
              {entityToEdit && (
                <button
                  onClick={handleDelete}
                  disabled={busy}
                  className="p-2 rounded-lg bg-[#C42251] text-white hover:bg-[#A01D45] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Eliminar entidad"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              )}
              <button onClick={onClose} className="p-2 rounded-lg bg-white/10 hover:bg-white/20" title="Cerrar">
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  value={tradeName}
                  onChange={(e) => setTradeName(e.target.value)}
                  placeholder="Ej: Fundación ABC, Corporación XYZ..."
                  title="Nombre con el que se conoce públicamente la entidad (nombre de fantasía)"
                  className="w-full h-11 pl-10 pr-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none transition-colors"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Región <span className="text-gray-500 text-xs font-normal">(requerido)</span>
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                    placeholder="Ingresa la región (ej: Región de Coquimbo, Metropolitana...)"
                    title="Debes ingresar la región donde se encuentra ubicada la entidad"
                    className="w-full h-11 pl-10 pr-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comuna <span className="text-gray-500 text-xs font-normal">(requerido)</span>
                </label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    value={district}
                    onChange={(e) => setDistrict(e.target.value)}
                    placeholder="Ingresa la comuna (ej: La Serena, Santiago, Valparaíso...)"
                    title="Debes ingresar la comuna o distrito específico donde se encuentra la entidad"
                    className="w-full h-11 pl-10 pr-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none transition-colors"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Logo <span className="text-gray-400 text-xs">(opcional)</span>
                </label>
                <div className="space-y-2">
                  {logoPreview ? (
                    <div className="relative">
                      <img
                        src={logoPreview}
                        alt="Preview"
                        className="w-full h-32 object-contain rounded-xl border border-gray-200 bg-gray-50"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveLogo}
                        className="absolute top-2 right-2 p-1.5 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors"
                        title="Eliminar logo"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full h-32 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-[#0488AF] hover:bg-[#0488AF]/5 transition-colors"
                    >
                      <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-600">Haz clic para subir logo</span>
                      <span className="text-xs text-gray-400 mt-1">JPG, PNG (máx. 5MB)</span>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="hidden"
                  />
                  {uploadingLogo && (
                    <div className="text-sm text-[#0488AF] flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Subiendo logo...
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                  Tipo de entidad <span className="text-gray-400 text-xs">(opcional)</span>
                  <TooltipHelper text="Selecciona el tipo de entidad de la lista o crea uno nuevo si no existe. El tipo de entidad ayuda a categorizar y filtrar las entidades." />
                </label>
                <div className="relative flex items-center gap-2">
                  <div className="relative flex-1">
                    <select
                      value={entityType}
                      onChange={(e) => setEntityType(e.target.value)}
                      title="Selecciona el tipo de entidad o crea uno nuevo si no existe en la lista"
                      className="w-full h-11 px-3 pr-8 rounded-xl border border-gray-300 bg-white focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none transition-colors text-sm"
                    >
                      <option value="">Seleccionar tipo</option>
                      {entityTypes.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowEntityTypesModal(true)}
                    className="h-11 px-3 flex items-center gap-2 rounded-xl border border-gray-300 bg-white hover:bg-gray-50 text-gray-600 hover:text-[#0488AF] transition-colors text-sm font-medium whitespace-nowrap"
                    title="Gestionar tipos de entidad"
                  >
                    <Settings className="w-4 h-4" />
                    <span className="hidden sm:inline">Gestionar</span>
                  </button>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                Público / Privado <span className="text-gray-400 text-xs">(opcional)</span>
                <TooltipHelper text="Público: entidad gubernamental o de acceso público (ej: fundaciones públicas, corporaciones estatales). Privado: entidad privada o corporativa (ej: empresas, ONGs privadas)." />
              </label>
              <Segmented
                ariaLabel="Público o Privado"
                value={isPublic === true ? true : isPublic === false ? false : undefined}
                onChange={(v) => setIsPublic(v === true ? true : v === false ? false : true)}
                options={[
                  { value: true, label: "Público", icon: <Landmark size={16} /> },
                  { value: false, label: "Privado", icon: <Briefcase size={16} /> },
                ]}
              />
            </div>

            <div>
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={isFavorite}
                  onChange={(e) => setIsFavorite(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-[#0488AF] focus:ring-[#0488AF]"
                />
                <span className="flex items-center gap-1 text-sm font-medium text-gray-700">
                  Es actor principal
                  <TooltipHelper text="Marca esta opción si esta entidad es considerada un actor principal. Las entidades marcadas como actor principal aparecerán destacadas y serán prioritarias en el seguimiento." />
                </span>
              </label>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button 
                type="button" 
                onClick={onClose} 
                className="h-11 px-4 rounded-xl bg-[#C42251] text-white hover:bg-[#A01D45] text-sm font-semibold transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={busy}
                className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed text-sm font-semibold transition-colors"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : entityToEdit ? 'Actualizar entidad' : 'Crear entidad'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modal de gestión de tipos de entidad */}
      {showEntityTypesModal && (
        <div className="fixed inset-0 z-[80]">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowEntityTypesModal(false)} />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="w-full max-w-md rounded-2xl bg-white border shadow-xl overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-[#0c3b66] to-[#0488AF] text-white flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Tag className="w-5 h-5" />
                  <h3 className="text-lg font-semibold">Gestionar tipos de entidad</h3>
                </div>
                <button
                  onClick={() => setShowEntityTypesModal(false)}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                {/* Lista de tipos de entidad */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tipos de entidad existentes</label>
                  {entityTypes.length > 0 ? (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {entityTypes.map((t) => (
                        <div
                          key={t.value}
                          className="flex items-center justify-between p-3 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                          <span className="text-sm font-medium text-gray-700">{t.label}</span>
                          <button
                            type="button"
                            onClick={() => handleDeleteEntityType(t.value)}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            title="Eliminar tipo de entidad"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic py-4 text-center">No hay tipos de entidad. Crea uno nuevo abajo.</p>
                  )}
                </div>

                {/* Agregar nuevo tipo de entidad */}
                <div className="pt-4 border-t border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Agregar nuevo tipo de entidad</label>
                  {!showNewEntityTypeInput ? (
                    <button
                      type="button"
                      onClick={() => setShowNewEntityTypeInput(true)}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#0488AF] px-4 py-2 text-sm font-medium text-white hover:bg-[#0c3b66] transition-colors"
                    >
                      <Plus size={16} />
                      Agregar tipo de entidad
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={newEntityTypeName}
                        onChange={(e) => setNewEntityTypeName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddEntityType();
                          }
                          if (e.key === 'Escape') {
                            setShowNewEntityTypeInput(false);
                            setNewEntityTypeName('');
                          }
                        }}
                        placeholder="Ej: Asociación, Cooperativa, ONG..."
                        className="w-full h-10 px-3 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none"
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handleAddEntityType}
                          className="flex-1 h-10 px-4 rounded-lg bg-[#0488AF] text-white hover:bg-[#037a98] text-sm font-medium transition-colors inline-flex items-center justify-center gap-1.5"
                        >
                          <Plus className="w-4 h-4" />
                          Guardar
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowNewEntityTypeInput(false);
                            setNewEntityTypeName('');
                          }}
                          className="h-10 px-4 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-sm font-medium text-gray-700 transition-colors"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
